import React, { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Input } from '@/components/atoms/Form/Input';
import { computeText } from '@/locales/utils';
import { SvySwitch } from '@/components/savory/SvySwitch';
import { Label } from '@/components/atoms/Label';
import { StepWrapper, SwitchWrapper, RadioGroup, RadioOption } from '../CreateRestaurantForm.style';

const ManagerStep = ({
    brandUsers,
}: {
    brandUsers: { userId: number; email: string; firstName: string; lastName: string }[];
}) => {
    const { control, setValue, getValues, clearErrors, trigger } = useFormContext();
    const intl = useIntl();
    const managerMode = useWatch({ control, name: 'managerMode' });
    const letUserChoosePassword = useWatch({ control, name: 'user.letUserChoosePassword' });

    useEffect(() => {
        if (letUserChoosePassword) {
            clearErrors(['user.password']);
        } else {
            const password = getValues('user.password');
            if (password) {
                trigger(['user.password']);
            }
        }
    }, [letUserChoosePassword, clearErrors, trigger, getValues]);

    return (
        <StepWrapper>
            <RadioGroup>
                {brandUsers.length > 0 && (
                    <RadioOption>
                        <input
                            type="radio"
                            checked={managerMode === 'existing'}
                            onChange={() => setValue('managerMode', 'existing')}
                        />
                        {computeText(intl, 'brandRestaurants.create.selectExistingUser')}
                    </RadioOption>
                )}
                <RadioOption>
                    <input
                        type="radio"
                        checked={managerMode === 'new'}
                        onChange={() => setValue('managerMode', 'new')}
                    />
                    {computeText(intl, 'brandRestaurants.create.createNewUser')}
                </RadioOption>
                <RadioOption>
                    <input
                        type="radio"
                        checked={managerMode === 'skip'}
                        onChange={() => setValue('managerMode', 'skip')}
                    />
                    {computeText(intl, 'brandRestaurants.create.skipManager')}
                </RadioOption>
            </RadioGroup>

            {managerMode === 'existing' && brandUsers.length > 0 && (
                <Controller
                    name="userId"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <select
                            value={value || ''}
                            onChange={(e) => {
                                const val = e.target.value;
                                onChange(val ? Number(val) : undefined);
                            }}
                            style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
                        >
                            <option value="">{computeText(intl, 'brandRestaurants.create.selectExistingUser')}</option>
                            {brandUsers.map((u) => (
                                <option key={u.userId} value={u.userId}>
                                    {u.firstName} {u.lastName} ({u.email})
                                </option>
                            ))}
                        </select>
                    )}
                />
            )}

            {managerMode === 'new' && (
                <>
                    <Controller
                        name="user.firstName"
                        control={control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <Input
                                name="user.firstName"
                                labelId="brands.form.firstName"
                                onChange={onChange}
                                value={value || ''}
                                error={error?.message}
                                required
                            />
                        )}
                    />
                    <Controller
                        name="user.lastName"
                        control={control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <Input
                                name="user.lastName"
                                labelId="brands.form.lastName"
                                onChange={onChange}
                                value={value || ''}
                                error={error?.message}
                                required
                            />
                        )}
                    />
                    <Controller
                        name="user.phone"
                        control={control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <Input
                                name="user.phone"
                                labelId="brandRestaurants.create.phone"
                                onChange={onChange}
                                value={value || ''}
                                error={error?.message}
                                required
                            />
                        )}
                    />
                    <Controller
                        name="user.email"
                        control={control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <Input
                                name="user.email"
                                labelId="brands.form.email"
                                onChange={onChange}
                                value={value || ''}
                                error={error?.message}
                                required
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="user.letUserChoosePassword"
                        render={({ field: { onChange, value } }) => (
                            <SwitchWrapper>
                                <SvySwitch value={value || false} onChange={onChange} />
                                <Label labelId="brandRestaurants.create.letUserChoosePassword" />
                            </SwitchWrapper>
                        )}
                    />
                    {!letUserChoosePassword && (
                        <Controller
                            name="user.password"
                            control={control}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <Input
                                    name="user.password"
                                    type="password"
                                    labelId="brands.form.password"
                                    onChange={onChange}
                                    value={value || ''}
                                    error={error?.message}
                                />
                            )}
                        />
                    )}
                </>
            )}
        </StepWrapper>
    );
};

export default ManagerStep;
